import { useCallback, useEffect, useRef, useState } from 'react';

/** Reveal off-screen content once, without replacing the element or its hover transform. */
export function useScrollReveal<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null);
  const [phase, setPhase] = useState<'pending' | 'visible'>();
  const hasRevealed = useRef(false);
  const ref = useCallback((node: T | null) => setElement(node), []);

  useEffect(() => {
    if (!element) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const bounds = element.getBoundingClientRect();
    // Content already on screen, including restored scroll positions, stays readable.
    if (hasRevealed.current || preference.matches || !('IntersectionObserver' in window)
      || (bounds.top < window.innerHeight && bounds.bottom > 0)) {
      hasRevealed.current = true;
      setPhase('visible');
      return;
    }

    const reveal = () => {
      hasRevealed.current = true;
      setPhase('visible');
      observer.disconnect();
    };
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) reveal();
    }, { threshold: 0, rootMargin: '0px 0px 48px 0px' });
    const updatePreference = () => { if (preference.matches) reveal(); };

    setPhase('pending');
    observer.observe(element);
    element.addEventListener('focusin', reveal);
    preference.addEventListener('change', updatePreference);
    return () => {
      observer.disconnect();
      element.removeEventListener('focusin', reveal);
      preference.removeEventListener('change', updatePreference);
    };
  }, [element]);

  return { ref, 'data-scroll-reveal': phase };
}
